"use client"

import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import {
  Grid,
  Box,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Skeleton,
  Fade,
  useTheme,
  alpha,
} from "@mui/material"
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
} from "@mui/icons-material"
import useConfig from "hooks/useConfig"
import useRequestIdGenerater from "customhooks"
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp"
import axios from "axios"

const ViewUploadedImages = ({ itemData, postId }) => {
  const theme = useTheme()
  const { borderRadius } = useConfig()
  const [clickedImages, setClickedImages] = useState([])
  const [openModal, setOpenModal] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [imageLoading, setImageLoading] = useState({})
  const RequestId = useRequestIdGenerater()
  const { rendomRequestId, generateTimeStamp } = useTimeStamp()

  const handleImageClick = (index) => {
    getPresignedUrl(index)
  }

  const getPresignedUrl = async (index) => {
    setLoading(true)
    try {
      const response = await axios.get(`/v1/posts/files?postId=${postId}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
          ),
        },
      })

      const formattedImages = response?.data?.data?.presignedUrl?.map((item, idx) => ({
        src: item,
        caption: itemData[idx]?.title || `Image ${idx + 1}`,
      }))

      setClickedImages(formattedImages)
      setCurrentImageIndex(index)
      setOpenModal(true)
      generateTimeStamp()
    } catch (error) {
      generateTimeStamp()
      console.error("Failed to fetch data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? clickedImages.length - 1 : prev - 1))
  }

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === clickedImages.length - 1 ? 0 : prev + 1))
  }

  const handleImageLoad = (index) => {
    setImageLoading((prev) => ({ ...prev, [index]: false }))
  }

  const handleImageLoadStart = (index) => {
    setImageLoading((prev) => ({ ...prev, [index]: true }))
  }

  const getGridColumns = (count) => {
    if (count === 1) return 12
    if (count === 2) return 6
    if (count === 3) return 4
    if (count === 4) return 6
    return 4
  }

  const getImageHeight = (count, index) => {
    if (count === 1) return 500
    if (count === 2) return 500
    if (count === 3) return index === 0 ? 500 : 500
    if (count === 4) return 500
    return 180
  }

  useEffect(() => {
    generateTimeStamp()
  }, [])

  if (!itemData || itemData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No images to display
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Grid container spacing={2} sx={{ p: 1 }}>
        {itemData.slice(0, 5).map((item, index) => (
          <Grid item xs={12} sm={getGridColumns(Math.min(itemData.length, 5))} key={index}>
            <Card
              sx={{
                position: "relative",
                cursor: "pointer",
                borderRadius: `${borderRadius}px`,
                overflow: "hidden",
                height: getImageHeight(Math.min(itemData.length, 5), index),
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: theme.shadows[8],
                  "& .image-overlay": {
                    opacity: 1,
                  },
                },
              }}
              onClick={() => handleImageClick(index)}
            >
              {imageLoading[index] && <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />}

              <CardMedia
                component="img"
                src={`data:image/*;base64,${item.data}`}
                alt={item.title || `Image ${index + 1}`}
                loading="lazy"
                onLoad={() => handleImageLoad(index)}
                onLoadStart={() => handleImageLoadStart(index)}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: imageLoading[index] ? "none" : "block",
                }}
              />

              {/* Overlay */}
              <Box
                className="image-overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: alpha(theme.palette.common.black, 0.4),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.3s ease-in-out",
                }}
              >
                <ZoomInIcon sx={{ color: "white", fontSize: 40 }} />
              </Box>

              {/* Image counter for multiple images */}
              {itemData.length > 5 && index === 4 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: alpha(theme.palette.common.black, 0.6),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h4" color="white" fontWeight="bold">
                    +{itemData.length - 5}
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Modal Dialog */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.9),
            boxShadow: "none",
            margin: 0,
            maxHeight: "100vh",
            maxWidth: "100vw",
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative", height: "100vh" }}>
          {/* Close Button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 1000,
              backgroundColor: alpha(theme.palette.common.white, 0.1),
              color: "white",
              "&:hover": {
                backgroundColor: alpha(theme.palette.common.white, 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Navigation Buttons */}
          {clickedImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1000,
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                  color: "white",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1000,
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                  color: "white",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          {/* Image Display */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            {clickedImages.length > 0 && (
              <Fade in={openModal} timeout={300}>
                <Box
                  component="img"
                  src={clickedImages[currentImageIndex]?.src}
                  alt={clickedImages[currentImageIndex]?.caption}
                  sx={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              </Fade>
            )}
          </Box>

          {/* Image Caption and Counter */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
              color: "white",
            }}
          >
            {clickedImages[currentImageIndex]?.caption && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {clickedImages[currentImageIndex].caption}
              </Typography>
            )}
            {clickedImages.length > 1 && (
              <Typography variant="body2" color="grey.300">
                {currentImageIndex + 1} of {clickedImages.length}
              </Typography>
            )}
          </Box>

          {/* Loading indicator */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <Typography color="white">Loading...</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

ViewUploadedImages.propTypes = {
  itemData: PropTypes.array.isRequired,
  postId: PropTypes.string.isRequired,
}

export default ViewUploadedImages
